import { UIRouterState } from '@ajs/ajs-upgraded-providers';
import AppStateService from '@ajs/services/AppStateService';
import FdxUI from '@ajs/services/fdxUI';
import ImpersonateUserService from '@ajs/services/ImpersonateUserService';
import { Component, HostBinding, Inject, OnInit, ViewChild } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { WINDOW } from '@app/core/providers/window.provider';
import { BigCommerceLinkService } from '@app/core/services/bigcommerce-link.service';
import { DateTimeService } from '@app/core/services/date-time.service';
import { FdxBlockUIService } from '@app/core/services/fdx-block-ui.service';
import { FdxUtilsService } from '@app/core/services/fdx-utils.service';
import { GcsMigrationService } from '@app/core/services/gcs-migration-service';
import { LinkService } from '@app/core/services/link.service';
import { SurfaceTierThemeService, SurfaceTierThemeType } from '@app/core/services/surface-tier-theme.service';
import { AbstractAppMenuComponent } from '@app/modules/nav/components/abstract-app-menu/abstract-app-menu.controller';
import { ActiveNavStateService } from '@app/modules/nav/services/active-nav-state.service';
import { NavStateService } from '@app/modules/nav/services/nav-state.service';
import { InvitationsService } from '@app/user/services/invitations.service';
import { UserDataService } from '@app/user/services/user-data.service';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faArrowLeftToLine, faArrowRightArrowLeft, faArrowRightFromBracket, faArrowTurnLeft, faArrowUpRightFromSquare, faChevronRight, faClockRotateLeft, faEye } from '@fortawesome/pro-solid-svg-icons';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { NgbAccordionDirective } from '@ng-bootstrap/ng-bootstrap/accordion/accordion.directive';
import { StateService } from '@uirouter/angularjs';
import { distinctUntilChanged, filter, takeUntil, tap } from 'rxjs';

@Component({
    selector: 'fdx-app-vertical-menu',
    templateUrl: './app-vertical-menu.component.html',
    styleUrls: ['./app-vertical-menu.component.scss', './app-vertical-menu-buttons.scss'],
    standalone: false
})
export class AppVerticalMenuComponent extends AbstractAppMenuComponent implements OnInit {
    @ViewChild('accordion') accordion: NgbAccordionDirective;
    @ViewChild('footerTooltip') footerTooltip: NgbTooltip;

    @HostBinding('class.collapsed') isNavBarCollapsed: boolean = false;

    isFooterMenuOpen: boolean = false;

    // The timeout for the copied account and db names state
    private copyTimeout: number | null = null;

    protected readonly faChevronRight: IconDefinition = faChevronRight;
    protected readonly faArrowLeftToLine: IconDefinition = faArrowLeftToLine;
    protected readonly faArrowUpRightFromSquare: IconDefinition = faArrowUpRightFromSquare;
    protected readonly faClockRotateLeft: IconDefinition = faClockRotateLeft;
    protected readonly faArrowRightFromBracket: IconDefinition = faArrowRightFromBracket;
    protected readonly faEye: IconDefinition = faEye;
    protected readonly faArrowTurnLeft: IconDefinition = faArrowTurnLeft;
    protected readonly faArrowRightArrowLeft: IconDefinition = faArrowRightArrowLeft;

    get logo(): string {
        return '/app/images/logos/feedonomics/feedonomics-logomark-white.svg';
    }

    currentSurfaceTier: SurfaceTierThemeType;
    nextSurfaceTier: SurfaceTierThemeType;

    get currentSurfaceTierBg(): string {
        return `bg-light-${this.currentSurfaceTier.bgLightTheme}`;
    }

    constructor(
        @Inject(WINDOW) protected readonly window: Window,
        @Inject(UIRouterState) protected readonly $state: StateService,   // Temporary for use with Ebay Selection and User Access Control pages being on AngularJS router
        private readonly activeNavState: ActiveNavStateService,
        protected readonly appStateService: AppStateService,
        protected readonly bigCommerceStoreLinkService: BigCommerceLinkService,
        protected readonly dateTimeService: DateTimeService,
        protected readonly fdxBlockUIService: FdxBlockUIService,
        protected readonly fdxUI: FdxUI,
        protected readonly fdxUtils: FdxUtilsService,
        protected readonly gcsMigrationService: GcsMigrationService,
        protected readonly impersonateUserService: ImpersonateUserService,   // TODO: Upgrade to Angular
        protected readonly invitationsService: InvitationsService,
        protected readonly linkService: LinkService,
        private readonly navState: NavStateService,
        private readonly surfaceTierThemeService: SurfaceTierThemeService,
        protected readonly userDataService: UserDataService,
        protected readonly router: Router
    ) {
        super($state, linkService, appStateService, fdxBlockUIService, fdxUI, fdxUtils, gcsMigrationService, impersonateUserService, invitationsService, userDataService, router, window);
    }

    ngOnInit(): void {
        super.ngOnInit();
        this.setActiveNavOnChanges();

        this.initAppStateService();

        this.initSurfacePLG();
    }

    setActiveNavOnChanges(): void {
        this.router.events
            .pipe(
                filter((event) => event instanceof NavigationEnd),
                tap(() => {
                    this.activeNavState.setActiveNav();
                }),
                takeUntil(this.unsubscribe$)
            ).subscribe();
    }

    private initAppStateService(): void {
        // When the user navigates to the account or database page, clear the navs
        this.clearNavInfoOnDatabaseUnset();

        // After the database has loaded, we should have all our available permissions - set the nav items the user is able to see.
        this.setNavInfoOnDatabaseSet();

        // Initialize the Surface PLG info
        this.initSurfacePLG();

        // When the account changes, we need to re-initialize the Surface PLG info
        this.appStateService.accountLoaded$.pipe(
            distinctUntilChanged((a, b) => a?.id === b?.id),
            filter((value) => value !== null),
            tap(() => this.initSurfacePLG()),
            takeUntil(this.unsubscribe$)
        ).subscribe();
    }

    private clearNavInfoOnDatabaseUnset(): void {
        this.appStateService.databaseLoaded$
            .pipe(
                distinctUntilChanged(),
                filter((value) => value === null),
                tap(() => {
                    this.navState.clearNavGroups();
                    this.activeNavState.clearActiveNavs();
                }),
                takeUntil(this.unsubscribe$)
            ).subscribe();
    }

    private setNavInfoOnDatabaseSet(): void {
        this.appStateService.databaseLoaded$
            .pipe(
                distinctUntilChanged((a, b) => a?.id === b?.id),
                filter((value) => value !== null),
                tap(() => {
                    this.navState.setNavigationGroups();

                    // On page load, vertical nav menu is displayed after the NavigationEnd router event
                    // So set this on first load.
                    this.activeNavState.setActiveNav();
                }),
                takeUntil(this.unsubscribe$)
            ).subscribe();
    }

    private initSurfacePLG(): void {
        this.currentSurfaceTier = this.surfaceTierThemeService.getCurrentTierThemeType();
        this.nextSurfaceTier = this.surfaceTierThemeService.getNextTierThemeType();
    }

    onToggleNavVisibility(): void {
        this.isNavBarCollapsed = !this.isNavBarCollapsed;
    }

    onFooterMenuOpenChange(isOpen: boolean): void {
        this.isFooterMenuOpen = isOpen;
        if (!isOpen) {
            this.hasCopiedNames = false;
        } else if (this.footerTooltip) {
            this.footerTooltip.close();
        }
    }

    copyDbAndAccountNames(): void {
        this.copyDBAndAccountName();
        if (this.copyTimeout) {
            this.window.clearTimeout(this.copyTimeout);
            this.copyTimeout = null;
        }

        this.copyTimeout = this.window.setTimeout(() => {
            this.hasCopiedNames = false;
        }, 5000);
    }
}
